import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Tag, ChevronLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatEUR, PROMO_CODE, PROMO_PERCENT, PROMO_MIN, lineTotal, itemDeal, FREE_SHIPPING_THRESHOLD, SHIPPING_FEE } from "@/lib/format";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [email, setEmail] = useState("");
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [loading, setLoading] = useState(false);

  const promoEligible = subtotal >= PROMO_MIN;
  const discount = appliedPromo && promoEligible ? Math.round(subtotal * (PROMO_PERCENT / 100)) : 0;
  const total = subtotal - discount;
  const shipping = total >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;
  const grandTotal = total + shipping;
  const remainingForFree = Math.max(0, FREE_SHIPPING_THRESHOLD - total);

  const applyPromo = () => {
    if (promo.trim().toUpperCase() !== PROMO_CODE) {
      toast.error("Code promo invalide");
      return;
    }
    if (subtotal < PROMO_MIN) {
      toast.error("Code non applicable", {
        description: `Le code ${PROMO_CODE} est valable dès ${formatEUR(PROMO_MIN)} d'achat.`,
      });
      return;
    }
    setAppliedPromo(PROMO_CODE);
    toast.success("Code promo appliqué !", { description: `-${PROMO_PERCENT}% sur votre commande` });
  };

  const pay = async () => {
    if (!acceptCgv) {
      toast.error("Veuillez accepter les Conditions Générales de Vente");
      return;
    }
    const buyerEmail = user && user.email ? user.email : email;
    if (!buyerEmail) {
      toast.error("Veuillez renseigner votre email");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
        promo_code: appliedPromo || null,
        user_id: user && user.id ? user.id : null,
        email: buyerEmail,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de paiement");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-28 text-center" data-testid="checkout-empty">
        <ShoppingBag className="h-16 w-16 text-stone-700 mx-auto mb-5" />
        <h1 className="font-display text-3xl text-stone-100 mb-3">Votre panier est vide</h1>
        <p className="text-stone-400 mb-8">Ajoutez des articles avant de passer commande.</p>
        <Link to="/boutique" className="btn-ember rounded-xl px-8 py-3.5 inline-block">Aller à la boutique</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-16" data-testid="checkout-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-stone-400 hover:text-amber-400 mb-6 text-sm">
        <ChevronLeft className="h-4 w-4" /> Continuer mes achats
      </button>
      <h1 className="font-display text-4xl font-black uppercase text-stone-100 mb-10">Finaliser la commande</h1>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-8">
          {/* Coordonnées */}
          <section className="gothic-card rounded-2xl p-6">
            <h2 className="font-display text-xl font-bold text-stone-100 mb-4">Vos coordonnées</h2>
            {user && user.email ? (
              <p className="text-stone-300 text-sm">
                Connecté en tant que <b className="text-amber-400">{user.email}</b>
              </p>
            ) : (
              <>
                <label className="block text-sm text-stone-400 mb-2">Email (pour le suivi de commande)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@email.fr"
                  data-testid="checkout-email"
                  className="w-full bg-[#0b0810] border border-amber-900/40 rounded-lg px-4 py-3 text-stone-100 focus:outline-none focus:border-amber-600"
                />
                <p className="text-xs text-stone-500 mt-3">
                  Vous avez un compte ?{" "}
                  <Link to="/connexion" className="text-amber-400 hover:underline">Se connecter</Link> — l'adresse de livraison
                  et le paiement sont saisis à l'étape sécurisée suivante.
                </p>
              </>
            )}
          </section>

          {/* Promo */}
          <section className="gothic-card rounded-2xl p-6">
            <h2 className="font-display text-xl font-bold text-stone-100 mb-4 flex items-center gap-2">
              <Tag className="h-5 w-5 text-amber-500" /> Code promo
            </h2>
            <div className="flex gap-3">
              <input
                value={promo}
                onChange={(e) => setPromo(e.target.value)}
                placeholder="FRISSON20"
                data-testid="promo-input"
                className="flex-1 bg-[#0b0810] border border-amber-900/40 rounded-lg px-4 py-3 text-stone-100 uppercase font-mono-tech focus:outline-none focus:border-amber-600"
              />
              <button onClick={applyPromo} data-testid="apply-promo-button" className="px-6 rounded-lg border-2 border-amber-500 text-amber-400 font-bold hover:bg-amber-500/10">
                Appliquer
              </button>
            </div>
            <p className="text-xs text-stone-500 mt-3">
              Code <b className="text-amber-400 font-mono-tech">{PROMO_CODE}</b> : -{PROMO_PERCENT}% dès {formatEUR(PROMO_MIN)} d'achat.
            </p>
            {appliedPromo && promoEligible && <p className="text-sm text-green-400 mt-2">✓ Code {appliedPromo} appliqué (-{PROMO_PERCENT}%)</p>}
            {appliedPromo && !promoEligible && (
              <p className="text-sm text-amber-400 mt-2" data-testid="promo-min-warning">
                Ajoutez {formatEUR(PROMO_MIN - subtotal)} pour activer le code {appliedPromo}.
              </p>
            )}
          </section>

          {/* CGV */}
          <section className="gothic-card rounded-2xl p-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptCgv}
                onChange={(e) => setAcceptCgv(e.target.checked)}
                data-testid="accept-cgv"
                className="mt-1 h-5 w-5 accent-amber-500"
              />
              <span className="text-sm text-stone-300">
                J'ai lu et j'accepte les{" "}
                <Link to="/cgv" target="_blank" className="text-amber-400 hover:underline">Conditions Générales de Vente</Link> et la{" "}
                <Link to="/confidentialite" target="_blank" className="text-amber-400 hover:underline">Politique de Confidentialité</Link>.
              </span>
            </label>
          </section>
        </div>

        {/* Résumé */}
        <div className="lg:col-span-2">
          <div className="gothic-card rounded-2xl p-6 lg:sticky lg:top-28">
            <h2 className="font-display text-xl font-bold text-stone-100 mb-5">Votre commande</h2>
            <div className="space-y-4 max-h-72 overflow-y-auto mb-5">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3" data-testid={`summary-item-${i.slug}`}>
                  <img src={i.image} alt={i.name} className="h-16 w-16 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-stone-100 truncate">{i.name}</p>
                    {i.size && <p className="text-xs text-stone-500">Taille : {i.size}</p>}
                    <p className="text-xs text-stone-500">Qté : {i.quantity}</p>
                    {itemDeal(i) && <p className="text-xs text-green-400">Offre {itemDeal(i).label}</p>}
                  </div>
                  <span className="font-mono-tech text-amber-400 text-sm">{formatEUR(lineTotal(i))}</span>
                </div>
              ))}
            </div>

            <div className="space-y-2 border-t border-amber-900/20 pt-4 text-sm">
              <div className="flex justify-between text-stone-300">
                <span>Sous-total</span>
                <span className="font-mono-tech">{formatEUR(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Remise ({PROMO_CODE})</span>
                  <span className="font-mono-tech">-{formatEUR(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-stone-400">
                <span>Livraison</span>
                <span className="font-mono-tech">{shipping === 0 ? "Offerte" : formatEUR(SHIPPING_FEE)}</span>
              </div>
              {remainingForFree > 0 && (
                <p className="text-xs text-amber-400/90" data-testid="free-shipping-hint">
                  Plus que {formatEUR(remainingForFree)} pour la livraison offerte 🎁
                </p>
              )}
              <div className="flex justify-between items-baseline border-t border-amber-900/20 pt-3 mt-3">
                <span className="font-bold text-stone-100">Total</span>
                <span className="font-mono-tech text-3xl font-extrabold text-amber-400" data-testid="checkout-total">{formatEUR(grandTotal)}</span>
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={pay}
              disabled={loading}
              data-testid="pay-button"
              className="btn-ember rounded-xl w-full py-4 mt-6 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Lock className="h-5 w-5" />
              {loading ? "Redirection…" : "Payer en toute sécurité"}
            </motion.button>
            <p className="text-center text-xs text-stone-500 mt-3">
              Paiement sécurisé par Stripe. Carte test : 4242 4242 4242 4242
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
